import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { MessagesNotificationService } from '../service/messages-notification.service';
import { LoggerService } from '../service/utils/logger.service';
import { isCapacitorNative } from '../service/platform/capacitor-platform';
import { PolicyAcceptanceStorageService } from '../service/legal/policy-acceptance-storage.service';
import { isTermsEndpointMissing } from '../service/legal/policy-terms.util';
import { PRIVACY_VERSION, TERMS_VERSION } from '../legal/legal-content';

@Component({
  selector: 'app-auth-callback',
  standalone: true,
  imports: [],
  template: '<p class="p-4 text-muted">Completing sign-in…</p>',
})
export class AuthCallbackComponent implements OnInit {
  constructor(
    private oauth: OAuthService,
    private router: Router,
    private restService: RestService,
    private authService: AuthService,
    private messagesNotification: MessagesNotificationService,
    private policyStorage: PolicyAcceptanceStorageService,
  ) {}

  ngOnInit(): void {
    this.oauth
      .loadDiscoveryDocumentAndTryLogin()
      .then(() => this.afterLogin())
      .catch((e) => {
        LoggerService.error('OIDC callback failed', e);
        void this.router.navigate(['/login']);
      });
  }

  private async afterLogin(): Promise<void> {
    if (!this.oauth.hasValidAccessToken()) {
      await this.router.navigate(['/login']);
      return;
    }
    const token = this.oauth.getAccessToken();
    if (!token) {
      await this.router.navigate(['/login']);
      return;
    }
    const profile = await this.restService.fetchAuthMe(token);
    if (!profile?.userId) {
      LoggerService.error('Auth me returned no profile', null);
      await this.router.navigate(['/login']);
      return;
    }
    const refreshToken = this.oauth.getRefreshToken?.() ?? null;
    const expiresAtRaw = this.oauth.getAccessTokenExpiration?.();
    const expiresAt = typeof expiresAtRaw === 'number' && expiresAtRaw > 0 ? expiresAtRaw : null;
    await this.authService.persistOidcLogin(profile, token, {
      provider: 'supabase-oauth',
      refreshToken,
      expiresAt,
    });
    let policyIsCurrent = false;
    let termsApiMissing = false;
    try {
      const policy = await this.restService.getCurrentTermsStatus(token);
      policyIsCurrent = policy.accepted;
      if (policyIsCurrent) {
        await this.policyStorage.save({
          userId: profile.userId,
          termsVersion: policy.policyVersion || TERMS_VERSION,
          privacyVersion: PRIVACY_VERSION,
          acceptedAt: new Date().toISOString(),
        });
      }
    } catch (e) {
      LoggerService.error('Could not read policy acceptance', e);
      termsApiMissing = isTermsEndpointMissing(e);
    }
    if (!policyIsCurrent && !termsApiMissing) {
      await this.router.navigate(['/accept-terms'], { replaceUrl: true });
      return;
    }
    const user = this.authService.getUser();
    if (user?.admin && !isCapacitorNative()) {
      await this.router.navigate(['/admin'], { replaceUrl: true });
      return;
    }
    const config = await this.restService.getContactConfig(user?.sid ?? '');
    const unreadCount = config?.unreadCount ?? 0;
    if (unreadCount > 0) {
      this.messagesNotification.setPendingLoginDialog(unreadCount);
    }
    await this.router.navigate(['/dashboard'], { replaceUrl: true });
  }
}
